/**
 * cliente-api — wrapper fetch dedicado pra endpoints /api/v1/cliente/* (B1.5).
 *
 * Diferenças vs `api.ts` (admin/interno):
 *   - SEM Authorization Bearer (auth via cookie httpOnly `igm_cliente_session`)
 *   - SEMPRE `credentials: 'include'` (browser anexa cookie automático)
 *   - Sem `setAuthToken` (cookie é setado pelo backend, não pelo frontend)
 *   - Sem dependência de `localStorage` (Regra inviolável: ZERO localStorage
 *     pra dados de negócio Portal Cliente — sessão é HTTP-only cookie)
 *
 * Uso:
 *   await clienteApi.post('/login', { email, senha });  // sem prefix /api/v1/cliente
 *   await clienteApi.get('/perfil');
 *
 * NÃO REVERTER:
 *   - credentials: 'include' obrigatório (sem isso cookie não vai)
 *   - prefix '/api/v1/cliente' aplicado automaticamente
 *   - response.json() retorna unknown — caller TYPE-CASTS pra interface
 *   - Erros 4xx/5xx lançam ClienteApiError com status + body
 */

/**
 * Espelha BASE_URL de `api.ts` (não exportado lá — duplicar é menor risco
 * do que adicionar export que mexe noutros files).
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const CLIENTE_PREFIX = '/api/v1/cliente';

/**
 * Cliente ATIVO setado pelo ClienteContext (B1.6 multi-cliente).
 * Usado automaticamente como header X-Client-Context em rotas autenticadas
 * que precisam de contexto (dashboard, chamados, orçamentos, etc).
 *
 * Pra rotas SEM context (login, perfil, meus-clientes, logout): irrelevante
 * — backend não exige header.
 *
 * Pode ser sobrescrito por opts.clientContext explicito numa call específica
 * (ex: admin queira testar X cliente sem trocar o ativo).
 */
let _activeClientContext: string | null = null;

export function setActiveClientContext(clientId: string | null) {
  _activeClientContext = clientId;
}

export function getActiveClientContext(): string | null {
  return _activeClientContext;
}

export class ClienteApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ClienteApiError';
    this.status = status;
    this.body = body;
  }

  /**
   * A frase que o servidor escreveu para a pessoa (`mensagem` no corpo), quando houver. Mora aqui, na classe dona do
   * `body`, para nenhuma tela ler `.body` de um erro capturado (trava `erro-tem-corpo-em-response`).
   */
  fraseDoServidor(): string | undefined {
    const m = (this.body as { mensagem?: unknown } | undefined)?.mensagem;
    return typeof m === 'string' && m.trim() ? m : undefined;
  }
}

interface ClienteApiOptions {
  /** Header opcional X-Client-Context (B1.6+ multi-cliente). */
  clientContext?: string;
  /** AbortSignal pra cancelamento. */
  signal?: AbortSignal;
}

async function request<T>(
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
  opts: ClienteApiOptions = {},
): Promise<T> {
  const fullUrl = `${BASE_URL}${CLIENTE_PREFIX}${path}`;
  const headers: Record<string, string> = {};

  // Body pode ser FormData (multipart upload — B1.6 chamados com foto)
  // ou objeto (JSON). FormData NÃO seta content-type — browser cuida do
  // boundary automaticamente.
  const isFormData = body instanceof FormData;
  if (body !== undefined && !isFormData) headers['content-type'] = 'application/json';

  // Header X-Client-Context: prioridade pra opts explícito, senão usa ativo
  // setado pelo ClienteContext via setActiveClientContext.
  const contextToUse = opts.clientContext ?? _activeClientContext;
  if (contextToUse) headers['X-Client-Context'] = contextToUse;

  const bodyToSend = isFormData
    ? (body as FormData)
    : body !== undefined
      ? JSON.stringify(body)
      : undefined;

  const response = await fetch(fullUrl, {
    method,
    headers,
    credentials: 'include', // CRUCIAL: anexa cookie httpOnly automático
    body: bodyToSend,
    signal: opts.signal,
  });

  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = text.length > 0 ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!response.ok) {
    const errMsg =
      (parsed as { error?: string; message?: string })?.message ??
      (parsed as { error?: string; message?: string })?.error ??
      `HTTP ${response.status}`;
    throw new ClienteApiError(response.status, errMsg, parsed);
  }

  return parsed as T;
}

export const clienteApi = {
  get: <T = unknown>(path: string, opts?: ClienteApiOptions) =>
    request<T>(path, 'GET', undefined, opts),

  post: <T = unknown>(path: string, body?: unknown, opts?: ClienteApiOptions) =>
    request<T>(path, 'POST', body, opts),

  put: <T = unknown>(path: string, body?: unknown, opts?: ClienteApiOptions) =>
    request<T>(path, 'PUT', body, opts),

  delete: <T = unknown>(path: string, opts?: ClienteApiOptions) =>
    request<T>(path, 'DELETE', undefined, opts),
};
